import React from 'react';
import { FetchDataProps } from './useAlgoliaSearch';
import { PageInfo } from '../models';

interface UseInfinityScrollProductsProps {
  getItems: (props: FetchDataProps) => Promise<any>;
  pageInfo: PageInfo;
}

type TUseInfinityScrollProducts<T> = {
  items: T[];
  loadItems: (searchText?: string) => Promise<void>;
  loading: boolean;
};

export function useInfinityScrollProducts<T>({
  getItems,
  pageInfo,
}: UseInfinityScrollProductsProps): TUseInfinityScrollProducts<T> {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const pageToLoad = React.useRef(0); /* 2 */
  const initialPageLoaded = React.useRef(false);

  const loadItems = async (searchText?: string) => {
    setLoading(true);

    const data = await getItems({
      newPage: Number(pageToLoad.current),
      searchText: searchText ?? '',
    });

    const currentPage = Number(data?.pageInfo?.currentPage) + 1;
    pageToLoad.current = !Number.isNaN(currentPage) ? currentPage : 0;

    // delete duplicate items
    if (searchText) {
      setItems(deleteDuplicateItems(data.items));
    } else {
      setItems((prevItems) =>
        deleteDuplicateItems([...prevItems, ...data.items])
      );
    }
    setLoading(false);
  };

  React.useEffect(() => {
    if (initialPageLoaded.current) {
      return;
    }
    loadItems();
    initialPageLoaded.current = true;
  }, []);

  return {
    items,
    loadItems,
    loading,
  };
}

function deleteDuplicateItems(items) {
  const ids = items.map((o) => o.objectID);
  const filtered = items.filter(
    ({ objectID }, index) => !ids.includes(objectID, index + 1)
  );
  return filtered;
}
